import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { LanguageProvider } from './context/LanguageContext';
import { CustomThemeProvider } from './context/ThemeContext';
import AuthPage from './components/Auth/AuthPage';
import Dashboard from './components/Dashboard/Dashboard';
import LoadingScreen from './components/Common/LoadingScreen';
import LegalPage from './components/Legal/LegalPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return isAuthenticated() ? <Navigate to="/dashboard" /> : children;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("React Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: 'red' }}>
          <h2>Ein unerwarteter Fehler ist aufgetreten (App abgestürzt).</h2>
          <details style={{ whiteSpace: 'pre-wrap', background: '#ffebee', padding: '1rem', borderRadius: '8px' }}>
            <summary>Fehlerdetails anzeigen</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
          <button onClick={() => window.location.href='/'} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
            Seite neu laden
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <CustomThemeProvider>
        <AuthProvider>
          <LanguageProvider>
            <SocketProvider>
              <Router>
                <Routes>
                  <Route 
                    path="/login" 
                    element={
                      <PublicRoute>
                        <AuthPage />
                      </PublicRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/*" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/agb" element={<LegalPage type="agb" />} />
                  <Route path="/datenschutz" element={<LegalPage type="privacy" />} />
                  <Route path="/privacy" element={<LegalPage type="privacy" />} />
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                </Routes>
              </Router>
            </SocketProvider>
          </LanguageProvider>
        </AuthProvider>
      </CustomThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
